import { useState, useEffect } from "react";
import { useUpdateUserMutation, useDeleteUserMutation } from "./usersApiSlice";
import { useParams, useNavigate } from "react-router-dom";
import { ROLES } from "../../config/roles";

const USER_REGEX = /^[A-z]{3,20}$/;
const DISPLAYNAME_REGEX = /^[A-Za-z\s]{3,20}$/;
const PWD_REGEX = /^[A-z0-9!@#$%]{4,12}$/;

export default function EditUserForm({ user }) {
  const { id } = useParams();                   // /dash/users/:id
  const navigate = useNavigate();

  const [updateUser, { isLoading, isSuccess, isError, error }] = useUpdateUserMutation();
  const [deleteUser, { isSuccess: isDelSuccess, isError: isDelError, error: delerror }] =
    useDeleteUserMutation();

  // Form state
  const [username, setUsername] = useState(user.username);
  const [displayname, setDisplayname] = useState(user.displayname);
  const [password, setPassword] = useState("");
  const [roles, setRoles] = useState(user.roles);
  const [active, setActive] = useState(user.active);

  // Validation flags
  const [validUsername, setValidUsername] = useState(false);
  const [validDisplayname, setValidDisplayname] = useState(false);
  const [validPassword, setValidPassword] = useState(false);

  // Validate on change
  useEffect(() => setValidUsername(USER_REGEX.test(username)), [username]);
  useEffect(() => setValidDisplayname(DISPLAYNAME_REGEX.test(displayname)), [displayname]);
  useEffect(() => setValidPassword(PWD_REGEX.test(password)), [password]);

  // After save/delete → go back
  useEffect(() => {
    if (isSuccess || isDelSuccess) {
      navigate("/dash/users");
    }
  }, [isSuccess, isDelSuccess, navigate]);

  const onRolesChanged = (e) => {
    const values = Array.from(e.target.selectedOptions, (o) => o.value);
    setRoles(values);
  };

  const canSave = password
    ? [roles.length, validUsername, validDisplayname, validPassword].every(Boolean) && !isLoading
    : [roles.length, validUsername, validDisplayname].every(Boolean) && !isLoading;

  const onSaveUserClicked = async (e) => {
    e.preventDefault();
    const targetId = user?._id || id;
    if (!targetId) return;

    const base = { id: targetId, username, displayname, roles, active };
    try {
      await updateUser(password ? { ...base, password } : base).unwrap();
    } catch (err) {
      // handled by error render
    }
  };

  const onDeleteUserClicked = async () => {
    const targetId = user?._id || id;
    if (!targetId) return;
    if (!window.confirm(`Delete user "${username}"?`)) return;
    try {
      await deleteUser(targetId).unwrap();
    } catch (err) {
      // handled by error render
    }
  };

  const errText = (error?.data?.message || delerror?.data?.message) ?? "";

  return (
    <div className="min-h-full px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        {/* <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Edit User</h1>
          <p className="text-sm text-gray-500">Update account details, roles, and status.</p>
        </div> */}
        {/* <div className="flex items-center gap-2">
          <button
            onClick={onDeleteUserClicked}
            className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Delete
          </button>
          <button
            onClick={onSaveUserClicked}
            disabled={!canSave}
            className={`rounded-xl px-4 py-2 text-sm text-white ${
              canSave ? "bg-gray-900 hover:bg-black" : "bg-gray-400 cursor-not-allowed"
            }`}
          >
            Save
          </button>
        </div> */}
      </div>

      {/* Error banner */}
      {(isError || isDelError) && (
        <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-rose-700">
          {errText || "Something went wrong."}
        </div>
      )}

      {/* Card form */}
      <form onSubmit={(e) => e.preventDefault()} className="mt-6">
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Username */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                Username <span className="text-gray-400 font-normal">[3–20 letters]</span>
              </label>
              <input
                id="username"
                type="text"
                autoComplete="off"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={`mt-1 w-full rounded-xl border ${
                  validUsername ? "border-gray-200" : "border-amber-300"
                } bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5`}
                placeholder="johndoe"
              />
              {!validUsername && (
                <p className="mt-1 text-xs text-amber-600">Letters only, 3–20 characters.</p>
              )}
            </div>

            {/* Display name */}
            <div>
              <label htmlFor="displayname" className="block text-sm font-medium text-gray-700">
                Display Name <span className="text-gray-400 font-normal">[3–20 letters]</span>
              </label>
              <input
                id="displayname"
                type="text"
                autoComplete="off"
                value={displayname}
                onChange={(e) => setDisplayname(e.target.value)}
                className={`mt-1 w-full rounded-xl border ${
                  validDisplayname ? "border-gray-200" : "border-amber-300"
                } bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5`}
                placeholder="John Doe"
              />
              {!validDisplayname && (
                <p className="mt-1 text-xs text-amber-600">Letters/spaces, 3–20 characters.</p>
              )}
            </div>

            {/* Password (optional) */}
            <div className="md:col-span-2">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password{" "}
                <span className="text-gray-400 font-normal">
                  [empty = no change, 4–12 chars incl. !@#$%]
                </span>
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`mt-1 w-full rounded-xl border ${
                  password && !validPassword ? "border-amber-300" : "border-gray-200"
                } bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5`}
                placeholder="••••••••"
              />
              {password && !validPassword && (
                <p className="mt-1 text-xs text-amber-600">
                  4–12 chars; letters, numbers, or !@#$%.
                </p>
              )}
            </div>

            {/* Roles */}
            <div className="md:col-span-2">
              <label htmlFor="roles" className="block text-sm font-medium text-gray-700">
                Assigned Roles
              </label>
              <select
                id="roles"
                multiple
                size="3"
                value={roles}
                onChange={onRolesChanged}
                className={`mt-1 w-full rounded-xl border ${
                  roles?.length ? "border-gray-200" : "border-amber-300"
                } bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-black/5`}
              >
                {Object.values(ROLES).map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
              {!roles?.length && (
                <p className="mt-1 text-xs text-amber-600">Select at least one role.</p>
              )}
            </div>

            {/* Active */}
            <div className="md:col-span-2">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  id="user-active"
                  name="user-active"
                  type="checkbox"
                  checked={active}
                  onChange={() => setActive((p) => !p)}
                  className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                Active
              </label>
            </div>
          </div>

          {/* Footer actions (duplicated for convenience) */}
          <div className="mt-6 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onDeleteUserClicked}
              className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Delete
            </button>
            <button
              type="submit"
              onClick={onSaveUserClicked}
              disabled={!canSave}
              className={`rounded-xl px-4 py-2 text-sm text-white ${
                canSave ? "bg-gray-900 hover:bg-black" : "bg-gray-400 cursor-not-allowed"
              }`}
            >
              Save
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
